export interface ContentData {
  nav: {
    home: string;
    about: string;
    academics: string;
    admissions: string;
    contact: string;
    gallery: string;
    administration: string;
    clubs: string;
    notice: string;
  };
  home: {
    hero: {
      title: string;
      subtitle: string;
      description: string;
    };
    features: {
      academic: string;
      cultural: string;
      technology: string;
      sports: string;
    };
    stats: {
      students: string;
      teachers: string;
      years: string;
    };
  };
  about: {
    title: string;
    mission: string;
    missionText: string;
    vision: string;
    visionText: string;
    values: {
      excellence: string;
      integrity: string;
      respect: string;
      innovation: string;
    };
  };
  academics: {
    title: string;
    primary: string;
    secondary: string;
    higher: string;
    curriculum: string;
    curriculumText: string;
  };
  admissions: {
    title: string;
    requirements: string;
    process: string;
    documents: string;
    fees: string;
  };
  contact: {
    title: string;
    address: string;
    phone: string;
    email: string;
    hours: string;
    sendMessage: string;
  };
  administration: {
    title: string;
    governingBody: string;
    chairman: string;
    principal: string;
    faculty: string;
    chairmanMessage: string;
    principalMessage: string;
  };
  clubs: {
    title: string;
    mathClub: string;
    scienceClub: string;
    debateClub: string;
    culturalClub: string;
    sportsClub: string;
    careerClub: string;
  };
  notice: {
    title: string;
    viewAll: string;
    latest: string;
    important: string;
    admission: string;
    exam: string;
    result: string;
  };
  footer: {
    copyright: string;
    privacy: string;
    terms: string;
    sitemap: string;
    quickLinks: string;
    contactInfo: string;
  };
}

export const contentData: Record<'en' | 'bn', ContentData> = {
  "en": {
    "nav": {
      "home": "Home",
      "about": "About",
      "academics": "Academics",
      "admissions": "Admissions",
      "contact": "Contact",
      "gallery": "Gallery",
      "administration": "Administration",
      "clubs": "Clubs",
      "notice": "Notice"
    },
    "home": {
      "hero": {
        "title": "Excellence in Education",
        "subtitle": "Nurturing minds, building futures",
        "description": "Welcome to our prestigious institution where we follow the Ministry of Education guidelines to provide quality education in Bangladesh."
      },
      "features": {
        "academic": "Academic Excellence",
        "cultural": "Cultural Heritage",
        "technology": "Modern Technology",
        "sports": "Sports & Activities"
      },
      "stats": {
        "students": "Students",
        "teachers": "Teachers",
        "years": "Years of Excellence"
      }
    },
    "about": {
      "title": "About Our School",
      "mission": "Our Mission",
      "missionText": "To provide quality education following the Ministry of Education guidelines while preserving our cultural heritage.",
      "vision": "Our Vision",
      "visionText": "To be a leading educational institution that prepares students for global challenges while maintaining our Bangladeshi identity.",
      "values": {
        "excellence": "Excellence",
        "integrity": "Integrity",
        "respect": "Respect",
        "innovation": "Innovation"
      }
    },
    "academics": {
      "title": "Academic Programs",
      "primary": "Primary Education",
      "secondary": "Secondary Education",
      "higher": "Higher Secondary",
      "curriculum": "Curriculum",
      "curriculumText": "Following the National Curriculum and Textbook Board (NCTB) guidelines"
    },
    "admissions": {
      "title": "Admissions",
      "requirements": "Requirements",
      "process": "Application Process",
      "documents": "Required Documents",
      "fees": "Fee Structure"
    },
    "contact": {
      "title": "Contact Us",
      "address": "Address",
      "phone": "Phone",
      "email": "Email",
      "hours": "Office Hours",
      "sendMessage": "Send Message"
    },
    "administration": {
      "title": "Administration",
      "governingBody": "Governing Body",
      "chairman": "Message from Chairman",
      "principal": "Message from Principal",
      "faculty": "Faculty & Staff Information",
      "chairmanMessage": "Welcome to our prestigious institution. As Chairman, I am committed to providing quality education that follows the Ministry of Education guidelines while preserving our cultural heritage.",
      "principalMessage": "Our mission is to nurture minds and build futures. We focus on academic excellence, character development, and preparing students for global challenges."
    },
    "clubs": {
      "title": "Student Clubs",
      "mathClub": "Math Club",
      "scienceClub": "Science Club",
      "debateClub": "Debate Club",
      "culturalClub": "Cultural Club",
      "sportsClub": "Sports Club",
      "careerClub": "Career Building Club"
    },
    "notice": {
      "title": "Notice Board",
      "viewAll": "View All Notices",
      "latest": "Latest Notices",
      "important": "Important Notices",
      "admission": "Admission Notices",
      "exam": "Examination Notices",
      "result": "Result Notices"
    },
    "footer": {
      "copyright": "© 2024 Rajuk College. All rights reserved. Developed By Zerodevs",
      "privacy": "Privacy Policy",
      "terms": "Terms of Service",
      "sitemap": "Sitemap",
      "quickLinks": "Quick Links",
      "contactInfo": "Contact Information"
    }
  },
  "bn": {
    "nav": {
      "home": "হোম",
      "about": "আমাদের সম্পর্কে",
      "academics": "শিক্ষা",
      "admissions": "ভর্তি",
      "contact": "যোগাযোগ",
      "gallery": "গ্যালারি",
      "administration": "প্রশাসন",
      "clubs": "ক্লাব",
      "notice": "নোটিশ"
    },
    "home": {
      "hero": {
        "title": "শিক্ষায় উৎকর্ষতা",
        "subtitle": "মনের বিকাশ, ভবিষ্যতের নির্মাণ",
        "description": "আমাদের প্রতিষ্ঠানে স্বাগতম যেখানে আমরা শিক্ষা মন্ত্রণালয়ের নির্দেশিকা অনুসরণ করে বাংলাদেশে মানসম্পন্ন শিক্ষা প্রদান করি।"
      },
      "features": {
        "academic": "শৈক্ষিক উৎকর্ষতা",
        "cultural": "সাংস্কৃতিক ঐতিহ্য",
        "technology": "আধুনিক প্রযুক্তি",
        "sports": "ক্রীড়া ও কার্যক্রম"
      },
      "stats": {
        "students": "ছাত্র-ছাত্রী",
        "teachers": "শিক্ষক",
        "years": "বছরের উৎকর্ষতা"
      }
    },
    "about": {
      "title": "আমাদের বিদ্যালয় সম্পর্কে",
      "mission": "আমাদের লক্ষ্য",
      "missionText": "শিক্ষা মন্ত্রণালয়ের নির্দেশিকা অনুসরণ করে মানসম্পন্ন শিক্ষা প্রদান করা এবং আমাদের সাংস্কৃতিক ঐতিহ্য সংরক্ষণ করা।",
      "vision": "আমাদের দৃষ্টিভঙ্গি",
      "visionText": "বাংলাদেশি পরিচয় বজায় রেখে বিশ্বব্যাপী চ্যালেঞ্জের জন্য ছাত্রদের প্রস্তুত করে একটি শীর্ষস্থানীয় শিক্ষা প্রতিষ্ঠান হওয়া।",
      "values": {
        "excellence": "উৎকর্ষতা",
        "integrity": "সততা",
        "respect": "সম্মান",
        "innovation": "নবীনতা"
      }
    },
    "academics": {
      "title": "শৈক্ষিক কার্যক্রম",
      "primary": "প্রাথমিক শিক্ষা",
      "secondary": "মাধ্যমিক শিক্ষা",
      "higher": "উচ্চ মাধ্যমিক",
      "curriculum": "পাঠ্যক্রম",
      "curriculumText": "জাতীয় শিক্ষাক্রম ও পাঠ্যপুস্তক বোর্ড (এনসিটিবি) নির্দেশিকা অনুসরণ"
    },
    "admissions": {
      "title": "ভর্তি",
      "requirements": "প্রয়োজনীয়তা",
      "process": "আবেদন প্রক্রিয়া",
      "documents": "প্রয়োজনীয় কাগজপত্র",
      "fees": "ফি কাঠামো"
    },
    "contact": {
      "title": "যোগাযোগ করুন",
      "address": "ঠিকানা",
      "phone": "ফোন",
      "email": "ইমেইল",
      "hours": "অফিসের সময়",
      "sendMessage": "বার্তা পাঠান"
    },
    "administration": {
      "title": "প্রশাসন",
      "governingBody": "শাসক পরিষদ",
      "chairman": "চেয়ারম্যানের বাণী",
      "principal": "অধ্যক্ষের বাণী",
      "faculty": "শিক্ষক ও কর্মচারী তথ্য",
      "chairmanMessage": "আমাদের প্রতিষ্ঠানে স্বাগতম। চেয়ারম্যান হিসেবে, আমি শিক্ষা মন্ত্রণালয়ের নির্দেশিকা অনুসরণ করে মানসম্পন্ন শিক্ষা প্রদান এবং আমাদের সাংস্কৃতিক ঐতিহ্য সংরক্ষণের প্রতিশ্রুতি দিচ্ছি।",
      "principalMessage": "আমাদের লক্ষ্য হল মনের বিকাশ এবং ভবিষ্যতের নির্মাণ। আমরা শৈক্ষিক উৎকর্ষতা, চরিত্র গঠন এবং বিশ্বব্যাপী চ্যালেঞ্জের জন্য ছাত্রদের প্রস্তুত করার উপর গুরুত্ব দিই।"
    },
    "clubs": {
      "title": "ছাত্র ক্লাব",
      "mathClub": "গণিত ক্লাব",
      "scienceClub": "বিজ্ঞান ক্লাব",
      "debateClub": "বিতর্ক ক্লাব",
      "culturalClub": "সাংস্কৃতিক ক্লাব",
      "sportsClub": "ক্রীড়া ক্লাব",
      "careerClub": "ক্যারিয়ার বিল্ডিং ক্লাব"
    },
    "notice": {
      "title": "নোটিশ বোর্ড",
      "viewAll": "সকল নোটিশ দেখুন",
      "latest": "সাম্প্রতিক নোটিশ",
      "important": "গুরুত্বপূর্ণ নোটিশ",
      "admission": "ভর্তি নোটিশ",
      "exam": "পরীক্ষা নোটিশ",
      "result": "ফলাফল নোটিশ"
    },
    "footer": {
      "copyright": "© ২০২৪ রাজউক কলেজ। সর্বস্বত্ব সংরক্ষিত। Developed By Zerodevs",
      "privacy": "গোপনীয়তা নীতি",
      "terms": "সেবার শর্তাবলী",
      "sitemap": "সাইটম্যাপ",
      "quickLinks": "দ্রুত লিংক",
      "contactInfo": "যোগাযোগের তথ্য"
    }
  }
};
